import { useMemo } from 'react'
import Big from 'big.js'
import { ICryptoCurrency, ICurrency } from '../../../api/types'
import { DEFAULT_ASSET_NETWORK, DEFAULT_BASE_CURRENCY, DEFAULT_CRYPTO_ASSET, formKeys } from '../constants'
import { useStreamerConfigContext } from '@/contexts/StreamerConfigContext/StreamerConfigContext'
import { DonationType, PaymentType } from '@/pages/Donation/api/streamer/types.ts'

type Props = {
  cryptoCurrencies: ICryptoCurrency[]
  baseCurrencies: ICurrency[]
}

const getDefaultNetwork = (currency: ICryptoCurrency | null) => {
  if (!currency?.networks?.length) {
    return null
  }

  if (currency.code === DEFAULT_CRYPTO_ASSET) {
    return currency.networks.find(network => network.code === DEFAULT_ASSET_NETWORK) ?? currency.networks[0]
  } else {
    return currency.networks[0]
  }
}

export const useDefaultValues = ({ cryptoCurrencies, baseCurrencies }: Props) => {
  const { config } = useStreamerConfigContext()
  const streamerBaseCurrency = config?.base_currency ?? DEFAULT_BASE_CURRENCY
  const configMinAmount = config?.text_donation_config?.min_amount
  const minAmount = useMemo(() => new Big(configMinAmount ?? 0).round(2, 3).toString(), [configMinAmount])

  const selectedCryptoAsset = useMemo(
    () => cryptoCurrencies.find(currency => currency.code === DEFAULT_CRYPTO_ASSET) ?? cryptoCurrencies[0] ?? null,
    [cryptoCurrencies]
  )
  const selectedNetwork = useMemo(() => getDefaultNetwork(selectedCryptoAsset), [selectedCryptoAsset])

  const baseCurrency = useMemo(
    () => baseCurrencies.find(currency => currency.code === streamerBaseCurrency) ?? baseCurrencies[0],
    [baseCurrencies, streamerBaseCurrency]
  )

  const defaultGoal = useMemo(() => {
    const defaultGoal = config?.goals?.find(goal => goal.is_default)
    return defaultGoal ? defaultGoal?.id : 'None'
  }, [config?.goals])

  return {
    [formKeys.isIncludeFee]: false,
    [formKeys.isAnon]: false,
    [formKeys.donationType]: DonationType.Text,
    [formKeys.paymentType]: PaymentType.Crypto,
    [formKeys.selectedCryptoAsset]: selectedCryptoAsset,
    [formKeys.selectedNetwork]: selectedNetwork,
    [formKeys.baseCurrency]: baseCurrency,
    [formKeys.fiatAmount]: minAmount,
    [formKeys.goal]: defaultGoal,
    [formKeys.customizeType]: [],
  }
}
